import React, { useState, useEffect } from 'react';
import { Activity, BrainCircuit, HeartPulse, Sparkles, CheckCircle, ArrowRight, ArrowLeft } from 'lucide-react';

export default function CoachOnboardingWizard({ onComplete }) {
  const [step, setStep] = useState(1);
  const [aiText, setAiText] = useState('');
  const [showButton, setShowButton] = useState(false);
  
  const fullAiMessage = "Atleta detectado en sobreentrenamiento. Sugiero descanso activo de 48h y reducir la carga aeróbica en un 30% la próxima semana.";

  // Typing effect para el paso 2
  useEffect(() => {
    if (step === 2) {
      setAiText('');
      setShowButton(false);
      let currentText = '';
      let i = 0;
      const intervalId = setInterval(() => {
        if (i < fullAiMessage.length) {
          currentText += fullAiMessage[i];
          setAiText(currentText);
          i++;
        } else {
          clearInterval(intervalId);
          setShowButton(true);
        }
      }, 35);
      return () => clearInterval(intervalId);
    } else {
      setTimeout(() => setShowButton(true), 600);
    }
  }, [step]);

  const finishOnboarding = () => {
    localStorage.setItem('coachOnboardingComplete', 'true');
    if (onComplete) onComplete();
  };

  return (
    <>
      <style>{`
        .onboarding-container {
          position: fixed; top: 0; left: 0; width: 100vw; height: 100vh;
          display: flex; flex-direction: column; justify-content: center; align-items: center;
          background: #07090f; overflow: hidden; z-index: 9999;
        }

        /* Mesh Gradient animado de fondo */
        .onboarding-bg {
          position: absolute; top: -50%; left: -50%; width: 200%; height: 200%;
          background: 
            radial-gradient(circle at 50% 50%, rgba(139, 92, 246, 0.15), transparent 40%),
            radial-gradient(circle at 30% 30%, rgba(16, 185, 129, 0.1), transparent 50%),
            radial-gradient(circle at 70% 60%, rgba(252, 76, 2, 0.08), transparent 50%);
          animation: bgRotate 30s infinite linear;
          z-index: 0;
        }

        @keyframes bgRotate {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        /* Glass Panel Premium */
        .onboarding-panel {
          position: relative; z-index: 10;
          max-width: 650px; width: 90%;
          background: rgba(15, 20, 30, 0.5);
          backdrop-filter: blur(20px);
          -webkit-backdrop-filter: blur(20px);
          border: 1px solid rgba(255, 255, 255, 0.08);
          border-top: 1px solid rgba(255, 255, 255, 0.15);
          border-radius: 24px; padding: 3rem;
          box-shadow: 0 30px 60px rgba(0, 0, 0, 0.6), inset 0 1px 0 rgba(255, 255, 255, 0.1);
          display: flex; flex-direction: column; align-items: center; text-align: center;
          transition: all 0.5s cubic-bezier(0.4, 0, 0.2, 1);
        }

        /* Transiciones entre pasos */
        .step-content {
          animation: slideUpFade 0.6s cubic-bezier(0.16, 1, 0.3, 1) forwards;
          display: flex; flex-direction: column; align-items: center; width: 100%;
        }

        @keyframes slideUpFade {
          0% { opacity: 0; transform: translateY(20px) scale(0.98); }
          100% { opacity: 1; transform: translateY(0) scale(1); }
        }

        /* Mockup Widgets */
        .triage-widget {
          width: 100%; background: rgba(0,0,0,0.4); border-radius: 12px; padding: 1rem;
          margin-bottom: 2rem; border: 1px solid rgba(255,255,255,0.05);
          display: flex; flex-direction: column; gap: 0.5rem;
        }

        .triage-row {
          display: flex; justify-content: space-between; align-items: center;
          padding: 0.8rem 1rem; border-radius: 8px; background: rgba(255,255,255,0.03);
          transition: all 0.3s;
        }

        .triage-row.alert {
          background: rgba(239, 68, 68, 0.15); border: 1px solid rgba(239, 68, 68, 0.3);
          box-shadow: 0 0 15px rgba(239, 68, 68, 0.2);
          animation: pulseRed 2s infinite;
        }

        @keyframes pulseRed {
          0% { box-shadow: 0 0 0 0 rgba(239, 68, 68, 0.4); }
          70% { box-shadow: 0 0 0 10px rgba(239, 68, 68, 0); }
          100% { box-shadow: 0 0 0 0 rgba(239, 68, 68, 0); }
        }

        .ai-chat-bubble {
          background: linear-gradient(145deg, rgba(139, 92, 246, 0.2), rgba(139, 92, 246, 0.05));
          border: 1px solid rgba(139, 92, 246, 0.3);
          border-radius: 16px; border-top-left-radius: 4px;
          padding: 1.2rem; text-align: left; margin-bottom: 2rem; width: 100%;
          color: #e2e8f0; font-family: 'Space Mono', monospace; font-size: 0.95rem;
          box-shadow: 0 10px 25px rgba(139, 92, 246, 0.1);
          min-height: 100px;
        }

        .typing-cursor {
          display: inline-block; width: 8px; height: 16px; background: #8b5cf6;
          animation: blink 1s step-end infinite; margin-left: 4px; vertical-align: middle;
        }

        @keyframes blink { 50% { opacity: 0; } }

        /* Icon Wrapper Mágico */
        .icon-glow-wrapper {
          position: relative; margin-bottom: 1.5rem;
          display: flex; justify-content: center; align-items: center;
        }

        .icon-glow-bg {
          position: absolute; width: 100px; height: 100px;
          background: radial-gradient(circle, var(--icon-color) 0%, transparent 70%);
          opacity: 0.3; filter: blur(20px); animation: breathe 4s ease-in-out infinite;
        }

        @keyframes breathe {
          0%, 100% { transform: scale(1); opacity: 0.2; }
          50% { transform: scale(1.2); opacity: 0.4; }
        }

        /* Premium Buttons */
        .btn-premium {
          background: linear-gradient(135deg, var(--btn-color) 0%, var(--btn-color-dark) 100%);
          color: white; border: none; padding: 1rem 2.5rem; border-radius: 30px;
          font-family: 'Outfit', sans-serif; font-size: 1.1rem; font-weight: 600;
          cursor: pointer; display: flex; align-items: center; gap: 0.5rem;
          transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1);
          box-shadow: 0 4px 15px var(--btn-shadow);
          opacity: 0; animation: fadeUpBtn 0.4s 0.3s forwards;
        }

        .btn-premium:hover {
          transform: translateY(-3px) scale(1.02);
          box-shadow: 0 10px 25px var(--btn-shadow-hover);
        }

        .btn-outline {
          background: transparent; color: #94a3b8; border: 1px solid rgba(255,255,255,0.1);
          padding: 1rem 1.5rem; border-radius: 30px; font-weight: 600; font-family: 'Outfit', sans-serif;
          cursor: pointer; transition: all 0.2s; opacity: 0; animation: fadeUpBtn 0.4s 0.3s forwards;
        }

        .btn-outline:hover { background: rgba(255,255,255,0.05); color: white; }

        @keyframes fadeUpBtn {
          to { opacity: 1; }
        }
      `}</style>

      <div className="onboarding-container">
        <div className="onboarding-bg"></div>

        <div className="onboarding-panel">
          
          {/* Progress Indicators */}
          <div style={{ display: 'flex', gap: '8px', marginBottom: '3rem' }}>
            {[1, 2, 3].map(i => (
              <div key={i} style={{
                width: i === step ? '40px' : '12px', height: '6px', borderRadius: '3px',
                background: step >= i ? (i===1 ? '#ef4444' : i===2 ? '#8b5cf6' : '#10b981') : 'rgba(255,255,255,0.1)',
                transition: 'all 0.4s cubic-bezier(0.16, 1, 0.3, 1)'
              }}></div>
            ))}
          </div>

          {/* PASO 1: TRIAGE */}
          {step === 1 && (
            <div className="step-content">
              <div className="icon-glow-wrapper" style={{ '--icon-color': '#ef4444' }}>
                <div className="icon-glow-bg"></div>
                <div style={{ background: 'rgba(239, 68, 68, 0.1)', border: '1px solid rgba(239, 68, 68, 0.3)', padding: '1.2rem', borderRadius: '20px' }}>
                  <HeartPulse size={40} color="#ef4444" strokeWidth={1.5} />
                </div>
              </div>

              <h2 style={{ fontFamily: 'Outfit', fontSize: '2.2rem', marginBottom: '1rem', fontWeight: 700 }}>Triage <span style={{ color: '#ef4444' }}>Fisiológico</span></h2>
              <p style={{ color: '#94a3b8', fontSize: '1.1rem', marginBottom: '2rem', maxWidth: '90%' }}>
                Se acabó revisar calendarios interminables. NousMentor detecta caídas en la VFC, mala calidad de sueño y picos de carga para <strong>alertarte antes de la lesión</strong>.
              </p>

              <div className="triage-widget">
                <div className="triage-row">
                  <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                    <div style={{width:'30px', height:'30px', borderRadius:'50%', background:'linear-gradient(45deg, #3b82f6, #60a5fa)'}}></div>
                    <span>Carlos M.</span>
                  </div>
                  <span style={{ color: '#10b981', fontSize: '0.9rem' }}>Recuperación Óptima</span>
                </div>
                <div className="triage-row alert">
                  <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                    <div style={{width:'30px', height:'30px', borderRadius:'50%', background:'linear-gradient(45deg, #ef4444, #f87171)'}}></div>
                    <span style={{fontWeight: 600, color: 'white'}}>Laura G.</span>
                  </div>
                  <span style={{ color: '#fca5a5', fontSize: '0.9rem', fontWeight: 600 }}>Alerta de Fatiga</span>
                </div>
              </div>

              {showButton && (
                <button className="btn-premium" 
                  style={{ '--btn-color': '#ef4444', '--btn-color-dark': '#dc2626', '--btn-shadow': 'rgba(239, 68, 68, 0.3)', '--btn-shadow-hover': 'rgba(239, 68, 68, 0.5)' }}
                  onClick={() => setStep(2)}>
                  Descubrir Copiloto <ArrowRight size={20} />
                </button>
              )}
            </div>
          )}

          {/* PASO 2: IA COPILOT */}
          {step === 2 && (
            <div className="step-content">
              <div className="icon-glow-wrapper" style={{ '--icon-color': '#8b5cf6' }}>
                <div className="icon-glow-bg"></div>
                <div style={{ background: 'rgba(139, 92, 246, 0.1)', border: '1px solid rgba(139, 92, 246, 0.3)', padding: '1.2rem', borderRadius: '20px' }}>
                  <BrainCircuit size={40} color="#a78bfa" strokeWidth={1.5} />
                </div>
              </div>

              <h2 style={{ fontFamily: 'Outfit', fontSize: '2.2rem', marginBottom: '1rem', fontWeight: 700 }}>Copiloto <span style={{ background: '-webkit-linear-gradient(45deg, #a78bfa, #c084fc)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>IA Avanzado</span></h2>
              <p style={{ color: '#94a3b8', fontSize: '1.1rem', marginBottom: '2rem', maxWidth: '90%' }}>
                Para cada atleta en Alerta, la Inteligencia Artificial cruzará docenas de biomarcadores para <strong>explicarte la causa exacta</strong> y sugerirte ajustes de entrenamiento.
              </p>

              <div className="ai-chat-bubble">
                <Sparkles size={16} color="#a78bfa" style={{ marginBottom: '8px' }} />
                <br/>
                {aiText}
                {step === 2 && aiText.length < fullAiMessage.length && <span className="typing-cursor"></span>}
              </div>

              {showButton && (
                <div style={{ display: 'flex', gap: '1rem' }}>
                  <button className="btn-outline" onClick={() => setStep(1)}><ArrowLeft size={20} /></button>
                  <button className="btn-premium" 
                    style={{ '--btn-color': '#8b5cf6', '--btn-color-dark': '#7c3aed', '--btn-shadow': 'rgba(139, 92, 246, 0.3)', '--btn-shadow-hover': 'rgba(139, 92, 246, 0.5)' }}
                    onClick={() => setStep(3)}>
                    Ver Siguiente <ArrowRight size={20} />
                  </button>
                </div>
              )}
            </div>
          )}

          {/* PASO 3: FINAL */}
          {step === 3 && (
            <div className="step-content">
              <div className="icon-glow-wrapper" style={{ '--icon-color': '#10b981' }}>
                <div className="icon-glow-bg"></div>
                <div style={{ background: 'rgba(16, 185, 129, 0.1)', border: '1px solid rgba(16, 185, 129, 0.3)', padding: '1.5rem', borderRadius: '50%' }}>
                  <CheckCircle size={56} color="#10b981" strokeWidth={1.5} />
                </div>
              </div>

              <h2 style={{ fontFamily: 'Outfit', fontSize: '2.5rem', marginBottom: '1rem', fontWeight: 700 }}>Estás <span style={{ color: '#10b981' }}>Listo</span></h2>
              <p style={{ color: '#94a3b8', fontSize: '1.15rem', marginBottom: '3rem', maxWidth: '80%' }}>
                Bienvenido al futuro del entrenamiento deportivo. Tu panel de control ha sido inicializado y configurado.
              </p>

              {showButton && (
                <button className="btn-premium" 
                  style={{ 
                    '--btn-color': '#10b981', '--btn-color-dark': '#059669', 
                    '--btn-shadow': 'rgba(16, 185, 129, 0.3)', '--btn-shadow-hover': 'rgba(16, 185, 129, 0.6)',
                    padding: '1.2rem 3rem', fontSize: '1.2rem', borderRadius: '40px'
                  }}
                  onClick={finishOnboarding}>
                  Acceder al Panel de Entrenador
                </button>
              )}
            </div>
          )}

        </div>
      </div>
    </>
  );
}
